import { useGetQSetTaskReviewResultsVerdictQuery, Verdict } from '@exam-management/graphql';
import { type Path, useNavigate, useParams } from '@exam-management/router';
import { Button, Flex, PaperSection, VisibilityOutlinedIcon } from '@shared/components';
import { matchPath, useLocation } from 'react-router-dom';

import {
  BaseQSetReviewResultList,
  QSetReviewResultOverviewCardGroup,
  type qSetReviewResultListColumn,
} from '../../components';
import { QSetReviewSubtaskReviewResult } from '../../constants';

export default function QSetReviewSummary() {
  const location = useLocation();
  const matchedTaskManagement = matchPath(
    '/task-management/qset-review/:taskId/*',
    location.pathname,
  );
  const paramPath = matchedTaskManagement
    ? '/task-management/qset-review/:taskId/subtask/:subTaskId/review-result'
    : '/my-task/qset-review/:taskId/subtask/:subTaskId/review-result';
  const { taskId, subTaskId } = useParams(paramPath);
  const navigate = useNavigate();

  const { data, loading: isLoading } = useGetQSetTaskReviewResultsVerdictQuery({
    variables: {
      getQSetTaskReviewResultsInput: {
        taskId: subTaskId,
        verdicts: [Verdict.Rejected, Verdict.Approved],
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const rows: qSetReviewResultListColumn[] =
    data?.wflGetTaskReviewResults.results.map((result) => {
      return {
        id: result.question.id,
        qid: result.question.questionRefCode,
        reviewResult: result.verdict?.verdict
          ? result.verdict.verdict === Verdict.Approved
            ? QSetReviewSubtaskReviewResult.Approved
            : QSetReviewSubtaskReviewResult.Rejected
          : null,
        comment: result.totalComments,
        order: result.question.order,
        viewDetails: (
          <Button
            size='small'
            variant='outlined'
            color='secondary'
            startIcon={<VisibilityOutlinedIcon />}
            sx={(theme) => ({
              p: theme.spacing(1.5, 3),
              width: '80px',
              maxHeight: '32px',
            })}
            onClick={() =>
              navigate(
                (matchedTaskManagement
                  ? `/task-management/qset-review/${taskId}/subtask/${subTaskId}/task-details/exam-details?order=${result.question.order}`
                  : `/my-task/qset-review/${taskId}/subtask/${subTaskId}/task-details/exam-details?order=${result.question.order}`) as Path,
                {
                  state: {
                    from: 'review-result',
                  },
                },
              )
            }
          >
            View
          </Button>
        ),
      };
    }) ?? [];

  const approvedCount = rows.filter(
    (row) => row.reviewResult === QSetReviewSubtaskReviewResult.Approved,
  ).length;
  const rejectedCount = rows.filter(
    (row) => row.reviewResult === QSetReviewSubtaskReviewResult.Rejected,
  ).length;
  const commentCount = rows.reduce((acc, row) => acc + row.comment, 0);

  return (
    <PaperSection title='Review Summary'>
      <Flex
        sx={(theme) => ({
          padding: theme.spacing(6),
          flexDirection: 'column',
          gap: theme.spacing(4),
        })}
      >
        <QSetReviewResultOverviewCardGroup
          cards={[
            {
              title: 'จำนวนข้อสอบทั้งหมด',
              description: `${rows.length} ข้อ`,
            },
            {
              title: 'เห็นด้วย',
              description: `${approvedCount}`,
              variant: 'success',
            },
            {
              title: 'ไม่เห็นด้วย',
              description: `${rejectedCount}`,
              variant: 'error',
            },
            {
              title: 'ความคิดเห็นทั้งหมด',
              description: `${commentCount}`,
              variant: 'info',
            },
          ]}
        />
        <BaseQSetReviewResultList rows={rows} loading={isLoading} />
      </Flex>
    </PaperSection>
  );
}
